import { useMemo, useState, useEffect } from 'react';
import styles from "./signers.module.scss";
import { SignersProps } from "../../../../interfaces/profile";
import { useToast } from "../../../../helpers/hooks";
import Button from "../../../../components/Button";
import TextFieldShared from "../../../../components/TextField/TextField";
import SelectShared from "../../../../components/Select/Select";
import { formatRut, validateRut } from '../../../../helpers/app';
import CheckIcon from '@mui/icons-material/Check';
import { images } from '../../../../constants/images';
import { ACCOUNT_TYPES } from '../../../../constants/profile';
import { UploadBankData, UploadSigners, ValidateBankData, ValidateSigners, useDisabledSaveBankData, useDisabledSaveSigners } from '../../../../constants/documentation';
import { CircularProgress, IconButton } from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';

const Signers = ({ 
	user,
	branch,
	getAllSigners,
	getBanks,
	banks,
	cleanBanks,
	patchBankData,
	cleanBankData,
	postSigners,
	putSigners,
	cleanSigners,
	signers,
	getRegions,
    regions,
	getAllCities,
	cities,
	getAllCommunes,
    communes,
	updateUser,
	getValidSignersDocuments, 
	valid,
	cleanValidSignersDocuments
}: SignersProps) => {
	const toast = useToast();
	const [loadingSigners, setLoadingSigners] = useState<boolean>(false);
	const [openSigners, setOpenSigners] = useState<boolean>(false);
	const [loadingBankData, setLoadingBankData] = useState<boolean>(false);
	const [isEmptySecondSigner, setIsEmptySecondSigner] = useState<boolean>(false);
	const [openBankData, setOpenBankData] = useState<boolean>(false);
	const [bankData, setBankData] = useState<any>({
		personal: {
			name: "",
			rut: "",
			email: ""
		},
		bank: {
			bankName: {
				value: "",
				error: ""
			},
			accountType: {
				value: "",
				error: ""
			},
			accountNumber: {
				value: "",
				error: ""
			}
		}
	});
	const [firstSigner, setFirstSigner] = useState<any>({
		id: "",
		lastUpdate: 0,
		name: {
			value: "",
			error: ""
		},
		lastName: {
			value: "",
			error: ""
		},
		rut: {
			value: "",
			error: ""
		},
		region: {
			value: {
				id: "",
				name: ""
			},
			error: ""
		},
		city: {
			value: {
				id: "",
				name: ""
			},
			error: "",
			state: "",
			options: []
		},
		commune: {
			value: {
				id: "",
				name: ""
			},
			error: "",
			state: "",
			options: []
		}
	});
	const [secondSigner, setSecondSigner] = useState<any>({
		id: "",
		lastUpdate: 0,
		name: {
			value: "",
			error: ""
		},
		lastName: {
			value: "",
			error: ""
		},
		rut: {
			value: "",
			error: ""
		},
		region: {
			value: {
				id: "",
				name: ""
			},
			error: ""
		},
		city: {
			value: {
				id: "",
				name: ""
			},
			error: "",
			state: "",
			options: []
		},
		commune: {
			value: {
				id: "",
				name: ""
			},
			error: "",
			state: "",
			options: []
		}
	});

	const handleChangeRut = (value: any, state: any, setState: any) => {
        const formatValue = formatRut(value);
		if(!validateRut(formatValue)){
            return setState({...state, rut: {value: formatValue, error: "Ingresa un rut válido"}});
        }
		setState({...state, rut: {value: formatValue, error: ""}});
    }

	const handleChangeRegion = (id: string, state: any, setState: any) => {
        const region = regionsNormalized && regionsNormalized.find((item: any) => item?.id === id);
		const citiesFiltered = citiesNormalized && citiesNormalized.filter((item: any) => item?.regionId === id);
		setState({
			...state, 
			region: {value: {id: region?.id, name: region?.name}, error: ""},
			city: {...state?.city, value: {id: "", name: ""}, error: "", options: citiesFiltered},
			commune: {...state?.commune, value: {id: "", name: ""}, error: "", options: []}
		});
    }

	const handleChangeCity = (id: string, state: any, setState: any) => {
        const city = citiesNormalized && citiesNormalized.find((item: any) => item?.id === id);
		const communesFiltered = communesNormalized && communesNormalized.filter((item: any) => item?.cityId === id);
		setState({
			...state, 
			city: {...state?.city, value: {id: city?.id, name: city?.name}, error: "", state: ""},
			commune: {...state?.commune, value: {id: "", name: ""}, error: "", options: communesFiltered}
		});
    }

    const handleChangeCommune = (id: string, state: any, setState: any) => {
        const commune = communesNormalized && communesNormalized.find((item: any) => item?.id === id);
		setState({...state, commune: {...state?.commune, value: {id: commune?.id, name: commune?.name}, error: ""}});
    }

	const saveBankData = () => {
		setLoadingBankData(true);
		const isValid = ValidateBankData(setBankData, bankData);

		if(!isValid){
			setLoadingBankData(false);
		}else{
			UploadBankData(user, bankData, patchBankData,);
		}
	};

	const saveSigners = () => {
		setIsEmptySecondSigner(false);
		setLoadingSigners(true);
		const res: any = ValidateSigners(
			setFirstSigner,
			firstSigner,
			setSecondSigner,
			secondSigner
		);

		if(!res?.valid){
			setLoadingSigners(false);
		}else{
			setIsEmptySecondSigner(res?.secondSigner === "empty")
			UploadSigners(
				signers,
				firstSigner,
				secondSigner,
				postSigners,
				putSigners,
				res?.secondSigner === "empty"
			);
		}
	};

	const regionsNormalized: any = useMemo(() => {
        return regions.data && regions.data.map((region: any) => (
            {
                id: region.region_id,
                name: region.name
            }
        ))
    }, [regions.data]);

	const citiesNormalized: any = useMemo(() => {
		return cities.data && cities.data.map((city: any) => (
			{
				id: city?.city_id,
				name: city?.name,
				regionId: city?.region_id
			}
		));
    }, [cities.data]);

	const communesNormalized: any = useMemo(() => {
        return communes.data && communes.data.map((commune: any) => (
            {
                id: commune?.commune_id,
                name: commune?.name,
				cityId: commune?.city_id
            }
        ))
    }, [communes.data]);

	useEffect(() => {
		if(banks?.fetching === "failure"){
			toast("Ha ocurrido un error al mostrar los bancos");
			cleanBanks();
		}
		if(banks?.fetching === "success"){
			cleanBanks();
		}
	}, [banks?.fetching]);

	useEffect(() => {
		if(signers?.bankData?.fetching === "failure"){
			toast("Ha ocurrido un error al guardar los datos bancarios");
			setLoadingBankData(false);
			cleanBankData();
		}
		if(signers?.bankData?.fetching === "success"){
			toast("Datos bancarios actualizados correctamente");
			getValidSignersDocuments();
			setLoadingBankData(false);
			updateUser(signers?.bankData?.data);
			cleanBankData();
		}
	}, [signers?.bankData?.fetching]);
	
	useEffect(() => {
		if(signers?.post?.fetching === "failure" || signers?.put?.fetching === "failure"){
			setLoadingSigners(false);
			toast("Ha ocurrido un error al actualizar los firmantes");
			cleanSigners();
		}
		if(signers?.post?.fetching === "success" || signers?.put?.fetching === "success"){
			getValidSignersDocuments();
		}
	}, [signers?.post?.fetching, signers?.put?.fetching]);
	
	useEffect(() => {
		if(signers?.count === 1 && isEmptySecondSigner){
			toast("Firmantes actualizados correctamente");
			setLoadingSigners(false);
			getAllSigners();
		}
		if(signers?.count === 2 && !isEmptySecondSigner){
			toast("Firmantes actualizados correctamente");
			setLoadingSigners(false);
			getAllSigners();
		}
	}, [signers?.count]);

	useEffect(() => {
		if(signers?.fetching === "failure"){
			toast("Ha ocurrido un error al mostrar los firmantes");
			cleanSigners();
		}
		if(signers?.fetching === "success"){
			let signerOne: any = [];
			let signerTwo: any = [];

			if(signers?.data && signers?.data?.length > 0){
				signerOne = signers?.data[0];
				signerTwo = signers?.data[1];
			}
			
			setFirstSigner({
				...firstSigner,
				lastUpdate: signerOne?.updated_at ? new Date(signerOne?.updated_at) : 0,
				id: signerOne?._id,
				name: {
					value: signerOne?.name ?? "",
					error: ""
				},
				lastName: {
					value: signerOne?.last_name ?? "",
					error: ""
				},
				rut: {
					value: signerOne?.legal_id ? formatRut(signerOne?.legal_id) : "",
					error: ""
				},
				region: {
					value: {
						id: signerOne?.residence?.region_id ?? "",
						name: signerOne?.residence?.region_name ?? ""
					},
					error: ""
				},
				city: {
					...firstSigner?.city,
					value: {
						id: signerOne?.residence?.city_id ?? "",
						name: signerOne?.residence?.city_name ?? ""
					},
					error: ""
				},
				commune: {
					...firstSigner?.commune,
					value: {
						id: signerOne?.residence?.commune_id ?? "",
						name: signerOne?.residence?.commune_name ?? ""
					},
					error: ""
				}
			});

			setSecondSigner({
				...secondSigner,
				lastUpdate: signerTwo?.updated_at ? new Date(signerTwo?.updated_at) : 0,
				id: signerTwo?._id,
				name: {
					value: signerTwo?.name ?? "",
					error: ""
				},
				lastName: {
					value: signerTwo?.last_name ?? "",
					error: ""
				},
				rut: {
					value: signerTwo?.legal_id ? formatRut(signerTwo?.legal_id) : "",
					error: ""
				},
				region: {
					value: {
						id: signerTwo?.residence?.region_id ?? "",
						name: signerTwo?.residence?.region_name ?? ""
					},
					error: ""
				},
				city: {
					...secondSigner?.city,
					value: {
						id: signerTwo?.residence?.city_id ?? "",
						name: signerTwo?.residence?.city_name ?? ""
					},
					error: ""
				},
				commune: {
					...secondSigner?.commune,
					value: {
						id: signerTwo?.residence?.commune_id ?? "",
						name: signerTwo?.residence?.commune_name ?? ""
					},
					error: ""
				}
			})
			
			getRegions();
			getAllCities();
			getAllCommunes();
			cleanSigners();
		}
	}, [signers?.fetching]);

	useEffect(() => {
		let citiesFiltered: any = [];
		let communesFiltered: any = [];

		citiesFiltered = citiesNormalized && citiesNormalized.filter((item: any) => item?.regionId === signers?.data[0]?.residence?.region_id);
		communesFiltered = communesNormalized && communesNormalized.filter((item: any) => item?.cityId === signers?.data[0]?.residence?.city_id);
		setFirstSigner({
			...firstSigner,
			city: {...firstSigner?.city, options: citiesFiltered},
			commune: {...firstSigner?.commune, options: communesFiltered}
		});
		
		citiesFiltered = citiesNormalized && citiesNormalized.filter((item: any) => item?.regionId === signers?.data[1]?.residence?.region_id);
		communesFiltered = communesNormalized && communesNormalized.filter((item: any) => item?.cityId === signers?.data[1]?.residence?.city_id);
		setSecondSigner({
			...secondSigner,
			city: {...secondSigner?.city, options: citiesFiltered},
			commune: {...secondSigner?.commune, options: communesFiltered}
		});
	}, [citiesNormalized, communesNormalized]);

	useEffect(() => {
		if(valid?.fetching === "failure"){
			toast("Ha ocurrido un error al validar su documentación");
			cleanValidSignersDocuments();
		}
		if(valid?.fetching === "success"){
			cleanValidSignersDocuments();
		}
	}, [valid?.fetching])

	useEffect(() => {
		window.scrollTo(0, 0);
		getValidSignersDocuments();
		getAllSigners();
		getBanks();
		setBankData({
			...bankData, 
			personal: {
				...bankData?.personal,
				name: `${branch?.data?.contact?.name ?? ''} ${branch?.data?.contact?.last_name ?? ''}`,
				rut: formatRut(branch?.data?.contact?.legal_id  ?? ''),
				email: branch?.data?.contact?.email
			},
			bank: {
				...bankData?.bank,
				bankName: {
					...bankData?.bank?.bankName,
					value: user?.bank_data?.bank ?? ''
				},
				accountType: {
					...bankData?.bank?.accountType,
					value: user?.bank_data?.account_type ?? ''
				},
				accountNumber: {
					...bankData?.bank?.accountNumber,
					value: user?.bank_data?.account_number.toString() ?? ''
				}
			}
		});
	}, []);

	return (
		<>
			<div className={`${styles.cardContent} ${styles.bankData} ${openBankData && styles.open}`}>
				<div className={styles.header} onClick={() => setOpenBankData(!openBankData)}>
					<p>
						<span className={styles.title}>Datos bancarios</span>
						<span className={styles.description}>
							El ingreso de tus datos de cuenta bancaria es obligatorio. Recuerda que debes ser el titular de esta cuenta.
						</span>
					</p>
					{
						valid?.fetching === "loading" &&
						<CircularProgress 
                            size={styles.loader}
                            classes={{
                                colorPrimary: styles.loader
                            }}
                        />
					}
					{
						valid?.fetching !== "loading" && valid?.data?.account &&
						<CheckIcon className={styles.valid} />
					}
					<IconButton className={styles.arrow} onClick={() => setOpenSigners(!openSigners)}>
						<ArrowBackIosNewIcon className={styles.icon} />
					</IconButton>
				</div>
				<div className={styles.signersContainer}>
					<div className={styles.signersColumn}>
						<div className={styles.signersData}>
							<p className={styles.subtitle}>Datos personales</p>
							<div className={styles.content}>
								<TextFieldShared
									label="Nombre completo"
									name="fullNamePersonal"
									value={bankData?.personal?.name}
									className={styles.full}
									disabled
								/>
								<TextFieldShared
									label="Rut"
									name="rutPersonal"
									value={bankData?.personal?.rut}
									className={styles.full}
									disabled
								/>
								<TextFieldShared
									label="Email"
									name="emailPersonal"
									value={bankData?.personal?.email}
									className={styles.full}
									disabled
								/>
							</div>
						</div>
						<div className={styles.horizontalLine}></div>
						<div className={styles.signersData}>
							<p className={styles.subtitle}>Datos bancarios</p>
							<div className={styles.content}>
							<SelectShared
								label="Nombre del Banco"
								name="bankName"
								value={bankData?.bank?.bankName?.value}
								onChange={(value: any) => setBankData({...bankData, bank: {...bankData?.bank, bankName: {...bankData?.bank?.bankName, value: value}}})}
								className={styles.full}
								options={banks?.banks}
								keyValue="name"
								loading={banks?.fetching === "loading"}
								error={bankData?.bank?.bankName?.error}
							/>
							<SelectShared
								label="Tipo de cuenta"
								name="accountType"
								value={bankData?.bank?.accountType?.value}
								onChange={(value: any) => setBankData({...bankData, bank: {...bankData?.bank, accountType: {...bankData?.bank?.accountType, value: value}}})}
								className={styles.full}
								options={ACCOUNT_TYPES}
								loading={banks?.fetching === "loading"}
								error={bankData?.bank?.accountType?.error}
							/>
							<TextFieldShared
								type="number"
								label="Número de cuenta"
								name="countNumber"
								value={bankData?.bank?.accountNumber?.value}
								onChange={(value: any) => setBankData({...bankData, bank: {...bankData?.bank, accountNumber: {...bankData?.bank?.accountNumber, value: value}}})}
								className={styles.full}
								error={bankData?.bank?.accountNumber?.error}
							/>
							</div>
						</div>
					</div>
					<div className={styles.submitButton}>
						<Button 
							onPress={saveBankData} 
							title="Guardar Cambios" 
							primary 
							className={styles.button}
							loading={loadingBankData}
							disabled={useDisabledSaveBankData(bankData)}
						/>
					</div>
				</div>
			</div>
			<div className={`${styles.cardContent} ${styles.signers} ${openSigners && styles.open}`}>
				<div className={styles.header} onClick={() => setOpenSigners(!openSigners)}>
					<p>
						<span className={styles.title}>Firmantes</span>
						<span className={styles.description}>
							Para garantizar la seguridad y la confianza en el proceso, requerimos la firma de dos personas firmantes. Por favor, complete los campos con la información solicitada.
						</span>
					</p>
					{
						valid?.fetching === "loading" &&
						<CircularProgress 
                            size={styles.loader}
                            classes={{
                                colorPrimary: styles.loader
                            }}
                        />
					}
					{
						valid?.fetching !== "loading" && valid?.data?.signers &&
						<CheckIcon className={styles.valid} />
					}
					<IconButton className={styles.arrow} onClick={() => setOpenSigners(!openSigners)}>
						<ArrowBackIosNewIcon className={styles.icon} />
					</IconButton>
				</div>
				<div className={styles.signersContainer}>
					<div className={styles.signersColumn}>
						<p className={styles.title}>Firmante 1</p>
						<div className={styles.horizontalLine}></div>
						<div className={styles.signersData}>
							<p className={styles.subtitle}>Datos personales</p>
							<div className={styles.content}>
								<TextFieldShared
									label="Nombre"
									name="nameFirstSigner"
									value={firstSigner?.name?.value}
									onChange={(value: any) => setFirstSigner({...firstSigner, name: {...firstSigner?.name, value: value}})}
									loading={regions?.fetching === "loading"}
									error={firstSigner?.name?.error}
									className={styles.half}
								/>
								<TextFieldShared
									label="Apellido"
									name="lastNameFirstSigner"
									value={firstSigner?.lastName?.value}
									onChange={(value: any) => setFirstSigner({...firstSigner, lastName: {...firstSigner?.lastName, value: value}})}
									loading={regions?.fetching === "loading"}
									error={firstSigner?.lastName?.error}
									className={styles.half}
								/>
								<TextFieldShared
									label="Rut"
									name="rutFirstSigner"
									value={firstSigner?.rut?.value}
									onChange={(value: any) => handleChangeRut(value, firstSigner, setFirstSigner)}
									loading={regions?.fetching === "loading"}
									error={firstSigner?.rut?.error}
									className={styles.full}
								/>
							</div>
						</div>
						<div className={styles.horizontalLine}></div>
						<div className={styles.signersData}>
							<p className={styles.subtitle}>Domicilio</p>
							<div className={styles.content}>
								<SelectShared
									label="Región"
									name="regionFirstSigner"
									value={firstSigner?.region?.value && firstSigner?.region?.value?.id}
									onChange={(value: any) => handleChangeRegion(value, firstSigner, setFirstSigner)}
									options={regionsNormalized}
									loading={regions?.fetching === "loading"}
									error={firstSigner?.region?.error && firstSigner?.region?.error}
									className={styles.full}
								/>
								<SelectShared
									label="Ciudad"
									name="cityFirstSigner"
									value={firstSigner?.city?.value && firstSigner?.city?.value?.id}
									onChange={(value: any) => handleChangeCity(value, firstSigner, setFirstSigner)}
									options={firstSigner?.city?.options}
									loading={regions?.fetching === "loading"}
									optionsEmptyMessage="Debes seleccionar una región"
									error={firstSigner?.city?.error && firstSigner?.city?.error}
									className={styles.full}
								/>
								<SelectShared
									label="Comuna"
									name="communeFirstSigner"
									value={firstSigner?.commune?.value && firstSigner?.commune?.value?.id}
									onChange={(value: any) => handleChangeCommune(value, firstSigner, setFirstSigner)}
									options={firstSigner?.commune?.options}
									loading={regions?.fetching === "loading"}
									optionsEmptyMessage="Debes seleccionar una ciudad"
									error={firstSigner?.commune?.error && firstSigner?.commune?.error}
									className={styles.full}
								/>
							</div>
						</div>
					</div>
					<div className={styles.signersColumn}>
						<p className={styles.title}>Firmante 2</p>
						<div className={styles.horizontalLine}></div>
						<div className={styles.signersData}>
							<p className={styles.subtitle}>Datos personales</p>
							<div className={styles.content}>
								<TextFieldShared
									label="Nombre"
									name="nameSecondSigner"
									value={secondSigner?.name?.value}
									onChange={(value: any) => setSecondSigner({...secondSigner, name: {...firstSigner?.name, value: value}})}
									loading={regions?.fetching === "loading"}
									error={secondSigner?.name?.error}
									className={styles.half}
								/>
								<TextFieldShared
									label="Apellido"
									name="lastNameSecondSigner"
									value={secondSigner?.lastName?.value}
									onChange={(value: any) => setSecondSigner({...secondSigner, lastName: {...firstSigner?.lastName, value: value}})}
									loading={regions?.fetching === "loading"}
									error={secondSigner?.lastName?.error}
									className={styles.half}
								/>
								<TextFieldShared
									label="Rut"
									name="rutSecondSigner"
									value={secondSigner?.rut?.value}
									onChange={(value: any) => handleChangeRut(value, secondSigner, setSecondSigner)}
									loading={regions?.fetching === "loading"}
									error={secondSigner?.rut?.error}
									className={styles.full}
								/>
							</div>
						</div>
						<div className={styles.horizontalLine}></div>
						<div className={styles.signersData}>
							<p className={styles.subtitle}>Domicilio</p>
							<div className={styles.content}>
								<SelectShared
									label="Región"
									name="regionSecondSigner"
									value={secondSigner?.region?.value && secondSigner?.region?.value?.id}
									onChange={(value: any) => handleChangeRegion(value, secondSigner, setSecondSigner)}
									options={regionsNormalized}
									loading={regions?.fetching === "loading"}
									error={secondSigner?.region?.error && secondSigner?.region?.error}
									className={styles.full}
								/>
								<SelectShared
									label="Ciudad"
									name="citySecondSigner"
									value={secondSigner?.city?.value && secondSigner?.city?.value?.id}
									onChange={(value: any) => handleChangeCity(value, secondSigner, setSecondSigner)}
									options={secondSigner?.city?.options}
									loading={regions?.fetching === "loading"}
									optionsEmptyMessage="Debes seleccionar una región"
									error={secondSigner?.city?.error && secondSigner?.city?.error}
									className={styles.full}
								/>
								<SelectShared
									label="Comuna"
									name="communeSecondSigner"
									value={secondSigner?.commune?.value && secondSigner?.commune?.value?.id}
									onChange={(value: any) => handleChangeCommune(value, secondSigner, setSecondSigner)}
									options={secondSigner?.commune?.options}
									loading={regions?.fetching === "loading"}
									optionsEmptyMessage="Debes seleccionar una ciudad"
									error={secondSigner?.commune?.error && secondSigner?.commune?.error}
									className={styles.full}
								/>
							</div>
						</div>
					</div>
					<div className={styles.submitButton}>
						<Button 
							onPress={saveSigners} 
							title="Guardar Cambios" 
							primary 
							className={styles.button}
							loading={loadingSigners}
							disabled={useDisabledSaveSigners(firstSigner, secondSigner)}
						/>
					</div>
				</div>
				<div className={styles.information}>
					<div className={styles.data}>
						<p className={styles.title}>A tener en cuenta</p>
						<p>
							<span>•</span>
							<span>Los firmantes desempeñarán un papel fundamental en la finalización exitosa de la venta del automóvil.</span>
						</p>
						<p>
							<span>•</span>
							<span>Sus firmas confirmarán la autenticidad de la información proporcionada y asegurarán a los compradores la veracidad de los datos relacionados con el vehículo.</span>
						</p>
						<p>
							<span>•</span>
							<span>Esto ayudará a generar confianza y transparencia en el proceso de compra, beneficiando tanto a la concesionaria como a los posibles compradores.</span>
						</p>
					</div>
					<div className={styles.ilustration}>
						<img src={images.signers} alt="firmantes" />
					</div>
				</div>
			</div>
		</>
	)
}

export default Signers;